import React, { useState } from 'react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { contact } from '../utils/api';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';
import { Mail, User, MessageSquare, Send } from 'lucide-react';

export const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await contact.submit(formData);
      toast.success('Message sent successfully');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message');
    }
    setLoading(false);
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="contact-page">
      <SEO title="Contact Us" description="Get in touch with Vintage Wardrobe Co. We'd love to hear from you — questions, collaborations, or just to say hello." path="/contact" />
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_6b1653b0-543f-41b7-adb3-d521cbe0d309/artifacts/ofj036nw_ChatGPT%20Image%20Mar%2022%2C%202026%2C%2010_24_56%20AM.png"
          alt="Vintage store"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/25 backdrop-blur-[3px]" />
      </div>

      {/* Form Card */}
      <div className="relative z-10 w-full max-w-md mx-4 my-12" data-testid="contact-form-card">
        <div className="bg-[#F9F8F6]/95 backdrop-blur-xl p-10 md:p-12 shadow-[0_12px_48px_rgba(0,0,0,0.12)] rounded-2xl border border-[#E2E2E2]">
          {/* Logo */}
          <div className="flex justify-center mb-5">
            <div className="relative">
              <div className="absolute -inset-2.5 bg-[#C9A84C]/15 rounded-full blur-md" />
              <div className="absolute -inset-0.5 rounded-full ring-2 ring-[#C9A84C]/25" />
              <img
                src="https://customer-assets.emergentagent.com/job_654bd78e-5e39-4822-a480-29d8c14fa990/artifacts/ow0h2qv9_Black%20and%20White%20Minimalist%20Round%20Beauty%20And%20Spa%20Logo.png"
                alt="Vintage Wardrobe Co"
                className="relative w-16 h-16 rounded-full object-cover shadow-lg bg-white"
                data-testid="contact-logo"
              />
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-1 text-center text-[#0A0A0A]" data-testid="contact-title">
            Get in Touch
          </h1>
          <p className="text-[#666] text-sm text-center mb-8">We'd love to hear from you</p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
            <div>
              <Label htmlFor="name" className="text-[#0A0A0A] font-medium text-xs uppercase tracking-widest mb-2 block">Name</Label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C9A84C]" />
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Your name"
                  className="bg-[#F0EEEB] border-[#E2E2E2] text-[#0A0A0A] placeholder:text-[#B5AA9A] pl-10 h-12 rounded-xl focus-visible:ring-[#C9A84C] focus-visible:border-[#C9A84C]"
                  data-testid="contact-name-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email" className="text-[#0A0A0A] font-medium text-xs uppercase tracking-widest mb-2 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C9A84C]" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="you@email.com"
                  className="bg-[#F0EEEB] border-[#E2E2E2] text-[#0A0A0A] placeholder:text-[#B5AA9A] pl-10 h-12 rounded-xl focus-visible:ring-[#C9A84C] focus-visible:border-[#C9A84C]"
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="message" className="text-[#0A0A0A] font-medium text-xs uppercase tracking-widest mb-2 block">Message</Label>
              <div className="relative">
                <MessageSquare className="absolute left-3.5 top-3.5 w-4 h-4 text-[#C9A84C]" />
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={4}
                  placeholder="How can we help?"
                  className="bg-[#F0EEEB] border-[#E2E2E2] text-[#0A0A0A] placeholder:text-[#B5AA9A] pl-10 resize-none rounded-xl focus-visible:ring-[#C9A84C] focus-visible:border-[#C9A84C]"
                  data-testid="contact-message-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#C9A84C] hover:bg-[#B08824] text-white text-sm uppercase tracking-widest font-bold transition-all duration-300 disabled:opacity-50 rounded-xl flex items-center justify-center gap-2 mt-2 shadow-[0_4px_16px_rgba(200,154,43,0.3)]"
              data-testid="contact-submit-button"
            >
              {loading ? 'Sending...' : <><Send className="w-4 h-4" /> Send Message</>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
